import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import CallOutlined from "@mui/icons-material/CallOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import DialogEditVenueIndividualContacts from "./DialogEditVenueIndividualContacts";
import { WhatsApp } from "@mui/icons-material";
import { IContactData, VenueData } from "../../types/venues.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { User } from "../../../../store/slice/AuthSlice";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";

interface ContactDetailsDialogProps {
  contact: IContactData | null;
  onEdit?: (updatedContact: IContactData) => void;
  inline?: boolean;
  showActionsInline?: boolean;
  venueData: VenueData | null;
}

const ContactDetailsDialog: React.FC<ContactDetailsDialogProps> = ({
  contact,
  onEdit,
  inline,
  showActionsInline,
  venueData,
}) => {
  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;
  const [isEditing, setIsEditing] = useState(false);
  const [currentContact, setCurrentContact] = useState<IContactData | null>(
    contact,
  );

  useEffect(() => {
    setCurrentContact(contact);
  }, [contact]);

  if (!contact) return null;

  const handleEditClick = () => setIsEditing(true);
  const handleEditComplete = (updatedContact: IContactData) => {
    setIsEditing(false);
    setCurrentContact(updatedContact);
    if (onEdit) onEdit(updatedContact);
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setCurrentContact(contact);
  };

  const toTitleCase = (str?: string | null) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  const handleRemoveContactFromVenue = async () => {
    if (!currentContact || !venueData) return;

    try {
      await apiService.patch(`/api/venues/venue/${venueData.id}/`, {
        remove_contact: currentContact.id,
      });

      toast.success("Contact removed from venue");
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove contact from venue");
    }
  };

  const renderContent = () => {
    if (isEditing) {
      if (!currentContact) {
        return null;
      }

      return (
        <Dialog
          open={isEditing}
          onClose={handleCancelEdit}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Contact</DialogTitle>
          <DialogContent>
            <DialogEditVenueIndividualContacts
              contact={currentContact || contact}
              onComplete={handleEditComplete}
              onCancel={handleCancelEdit}
            />
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Box
        sx={{
          borderRadius: 3,
          boxShadow: 0,
          p: 2,
          minHeight: inline ? 150 : 300,
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          textAlign: "center",
        }}
      >
        <Avatar
          sx={{
            width: inline ? 56 : 80,
            height: inline ? 56 : 80,
            mx: "auto",
            mb: 1,
            bgcolor: currentContact?.contact_image
              ? "transparent"
              : "primary.main",
          }}
          src={currentContact?.contact_image || undefined}
        >
          {!currentContact?.contact_image && (
            <PersonOutline sx={{ fontSize: inline ? 28 : 42 }} />
          )}
        </Avatar>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {`${toTitleCase(currentContact?.first_name)} ${toTitleCase(
            currentContact?.last_name,
          )}`}
        </Typography>

        {currentContact?.email && (
          <Typography variant="body2" color="text.secondary">
            {currentContact.email}
          </Typography>
        )}
        {currentContact?.phone && (
          <Typography variant="body2" color="text.secondary">
            {currentContact.phone}
          </Typography>
        )}

        {(!inline || showActionsInline) && (
          <>
            <Divider sx={{ my: 1.5, opacity: 0.2 }} />
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}
            >
              {currentContact?.phone && (
                <IconButton
                  onClick={() => {
                    const phone = (currentContact.phone || "").replace(
                      /\D/g,
                      "",
                    );

                    const firstName = currentContact.first_name
                      ? toTitleCase(currentContact.first_name)
                      : "there";

                    const message = `Dear ${firstName},
This is ${user?.first_name} ${user?.last_name} from Muzukuru Funeral Streaming.
I am reaching out about ${venueData?.name}`;

                    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

                    window.open(url, "_blank");
                  }}
                  sx={{
                    color: "#25D366",
                    "&:hover": {
                      backgroundColor: "rgba(37, 211, 102, 0.12)",
                    },
                  }}
                >
                  <WhatsApp />
                </IconButton>
              )}

              {currentContact?.email && (
                <IconButton
                  onClick={() => window.open(`mailto:${currentContact.email}`)}
                  sx={{
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                    },
                  }}
                >
                  <EmailOutlined />
                </IconButton>
              )}

              {currentContact?.phone && (
                <IconButton
                  onClick={() => window.open(`tel:${currentContact.phone}`)}
                  sx={{
                    color: "#2e7d32",
                    "&:hover": {
                      backgroundColor: "rgba(46, 125, 50, 0.12)",
                    },
                  }}
                >
                  <CallOutlined />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleEditClick}
              >
                Edit
              </Button>

              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleRemoveContactFromVenue}
              >
                Remove
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  };

  if (inline) {
    return (
      <Box
        sx={{
          width: 330,
          maxWidth: "100%",
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: 2,
          p: 1,
        }}
      >
        {renderContent()}
      </Box>
    );
  }

  return <>{renderContent()}</>;
};

export default ContactDetailsDialog;
